/**
 * The Countdown app's CLOCK (feature 0041) — framework-free. No React, no DOM beyond `localStorage`, so
 * BOTH consumers can share it:
 *  - `countdownEngine`'s React hook, which drives the buy-box banner, the bar and the popup;
 *  - the storefront's vanilla badge ticker, which animates the chips on a collection grid WITHOUT
 *    hydrating two dozen React roots (a grid of 24 cards must not cost 24 hydration passes).
 *
 * Both surfaces therefore agree by construction: the same evergreen storage key, the same expiry rules,
 * the same DAILY roll. A second implementation would drift, and a visitor would see two clocks
 * disagreeing on one page — the exact fault this app exists to avoid.
 */
/** Split whole seconds into D/H/M/S (clamped at 0 — a countdown never shows negative time). */
export function splitUnits(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    return {
        days: Math.floor(s / 86_400),
        hours: Math.floor(s / 3_600) % 24,
        minutes: Math.floor(s / 60) % 60,
        seconds: s % 60,
    };
}
/** Two-digit pad — the row must never change width as values cross 10. */
export function pad2(n) {
    return n > 9 ? String(n) : `0${n}`;
}
/** The DETERMINISTIC first-render remaining seconds — server props only, so SSR and hydration agree. */
export function initialRemainingSeconds(spec) {
    if (spec.timerType === 'EVERGREEN')
        return (spec.minutes ?? 0) * 60;
    return Math.max(0, Math.floor(((spec.endsAtEpochMs ?? 0) - spec.serverNowEpochMs) / 1000));
}
const EVERGREEN_STORAGE_PREFIX = 'aqx-cd-';
const DAY_MS = 86_400_000;
/**
 * Read/establish the visitor's evergreen deadline — the anti-Shopyan property: created on first sight,
 * SURVIVES reloads, and an expired one STAYS expired unless the campaign restarts. Storage failures
 * (private browsing, quota) degrade to a fresh in-memory deadline: the Shopyan behaviour, but only there.
 */
export function evergreenDeadline(spec, now) {
    const key = EVERGREEN_STORAGE_PREFIX + spec.campaignId;
    const minutes = spec.minutes ?? 0;
    const durationMs = minutes * 60_000;
    let stored;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw !== null) {
            const parsed = JSON.parse(raw);
            const deadline = Number(parsed?.deadline);
            // The duration is stored WITH the deadline. A deadline still running that was built from a different
            // duration is the seller's PREVIOUS setting: dropped, so shortening "2 heures" to "10 minutes"
            // actually reaches the visitors who already saw the campaign — otherwise the edit is ignored for
            // them, forever, and the seller watches their change do nothing. An ALREADY EXPIRED deadline is left
            // alone whatever the duration says: expired stays expired (the anti-Shopyan property).
            if (Number.isFinite(deadline) && (deadline <= now || Number(parsed?.minutes) === minutes))
                stored = deadline;
        }
    }
    catch {
        /* private browsing / quota — in-memory fallback below */
    }
    if (stored !== undefined && stored > now)
        return stored;
    if (stored !== undefined && spec.onExpiry !== 'RESTART')
        return stored;
    const fresh = now + durationMs;
    try {
        window.localStorage.setItem(key, JSON.stringify({ deadline: fresh, minutes }));
    }
    catch {
        /* fine — the in-memory deadline still gives this visit a stable countdown */
    }
    return fresh;
}
/** Establish the anchors at mount — evergreen from the visitor's own deadline, the rest server-anchored. */
export function anchorsFor(spec, now) {
    return spec.timerType === 'EVERGREEN'
        ? { offset: 0, deadline: evergreenDeadline(spec, now) }
        : { offset: spec.serverNowEpochMs - now, deadline: spec.endsAtEpochMs ?? 0 };
}
/**
 * Advance the clock. MUTATES [anchors] when the deadline rolls (DAILY) or renews (evergreen RESTART) —
 * the surface owns the object, this owns the rule.
 */
export function tickOnce(spec, anchors, now = Date.now()) {
    let left = anchors.deadline - (now + anchors.offset);
    if (left <= 0) {
        if (spec.timerType === 'DAILY') {
            // Roll to the next day locally; the next SSR recomputes it in the store timezone (DST-exact).
            while (anchors.deadline - (now + anchors.offset) <= 0)
                anchors.deadline += DAY_MS;
            left = anchors.deadline - (now + anchors.offset);
        }
        else if (spec.timerType === 'EVERGREEN' && spec.onExpiry === 'RESTART') {
            anchors.deadline = evergreenDeadline(spec, now);
            left = anchors.deadline - now;
        }
        else if (spec.onExpiry === 'HIDE') {
            return { remaining: 0, hidden: true };
        }
        else {
            return { remaining: 0, hidden: false };
        }
    }
    return { remaining: Math.floor(left / 1000), hidden: false };
}
//# sourceMappingURL=countdownClock.js.map